import React, { useState, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

import '../../css/documentation.css';

import GetStartedDocs from './documentation/GetStartedDocs';
import ReportABugDocs from './documentation/ReportABugDocs';

import ScheduleStructureDocs from './documentation/scheduling_wizard/ScheduleStructureDocs';
import UploadDataDocs from './documentation/scheduling_wizard/UploadDataDocs';
import ManageDepartmentsDocs from './documentation/scheduling_wizard/ManageDepartmentsDocs';
import EditConstraintsDocs from './documentation/scheduling_wizard/EditConstraintsDocs';
import CreateSectionsDocs from './documentation/scheduling_wizard/CreateSectionsDocs';
import PlaceSectionsDocs from './documentation/scheduling_wizard/PlaceSectionsDocs';
import PlaceStudentsDocs from './documentation/scheduling_wizard/PlaceStudentsDocs';
import MagnetboardDocs from './documentation/magnetboard/MagnetboardDocs';
import CoursesDocs from './documentation/school_data/CoursesDocs';
import TeachersDocs from './documentation/school_data/TeachersDocs';
import ClassroomsDocs from './documentation/school_data/ClassroomsDocs';
import StudentsDocs from './documentation/school_data/StudentsDocs';
import RequestsDocs from './documentation/scheduling_data/RequestsDocs';
import SectionsDocs from './documentation/scheduling_data/SectionsDocs';

export default function Documentation({ match, isAuth, authComplete, schoolInfoLoading, userInfo, schoolType, setLoadScreen,  scrollToInstructions }) {
	let history = useHistory();
	
	const [isLoading, setIsLoading] = useState(true);
	const [screen, setScreen] = useState((!match.params.screen || match.params.screen === 'null') ? 'getting_started' : match.params.screen);
	const [subscreen, setSubscreen] = useState((!match.params.subscreen || match.params.subscreen === 'null') ? null : match.params.subscreen);
	const [topic, setTopic] = useState((!match.params.topic || match.params.topic === 'null') ? null : match.params.topic);
	const [sis, setSis] = useState(match.params.sis ? match.params.sis : null);
	const [openScreenTab, setOpenScreenTab] = useState(screen);
	const magnetboardTopicFromUrl = (
		screen === 'magnetboard' &&
		subscreen &&
		!['magnetboard', 'null'].includes(subscreen)
	) ? subscreen : topic;
	const isSupervisor = userInfo && `${userInfo.role_id}` === '3';

	useEffect(() => {
		const nextScreen = (!match.params.screen || match.params.screen === 'null') ? 'getting_started' : match.params.screen;
		const nextSubscreen = (!match.params.subscreen || match.params.subscreen === 'null') ? null : match.params.subscreen;
		const nextTopic = (!match.params.topic || match.params.topic === 'null') ? null : match.params.topic;
		const nextSis = match.params.sis ? match.params.sis : null;

		setScreen(nextScreen);
		setSubscreen(nextSubscreen);
		setTopic(nextTopic);
		setSis(nextSis);
		setOpenScreenTab(nextScreen);
	}, [match.params.screen, match.params.subscreen, match.params.topic, match.params.sis]);

	// Permissions 
	// 1 -> super_admin
	// 2 -> admin
	// 3 -> supervisors
	// 4 -> counselors
	const app_screens = [
		{screen:'getting_started', name:'Getting Started', permissions:['1','2','3','4','5'], subscreens:[]},
		{screen:'scheduling_wizard', name:'Scheduling Wizard', permissions:['1', '2', '3', '5'], subscreens: [ 
			{subscreen:'schedule_structure', name:'Schedule Structure', permissions:['1','2','5']}, 
			{subscreen:'upload_data', name:'Uploading Data', permissions:['1','2','5']},  
			{subscreen:'manage_departments', name:'Manage Departments', permissions:['1','2','5']}, 
			{subscreen:'edit_constraints', name:'Edit Constraints', permissions:['1','2','3','5']}, 
			{subscreen:'create_sections', name:'Create Sections', permissions:['1','2','3','5']}, 
			{subscreen:'place_sections', name:'Place Sections', permissions:['1','2','3','5']}, 
			{subscreen:'place_students', name:'Place Students', permissions:['1','2','5']}
		]},
		{screen:'school_data', name:'School Data', permissions:['1','2','3','4','5'], subscreens: [ 
			{subscreen:'courses', name:'Courses', permissions:['1','2','3','5']},
			{subscreen:'teachers', name:'Teachers', permissions:['1','2','3','5']},
			{subscreen:'classrooms', name:'Classrooms', permissions:['1','2','3','5']},
			{subscreen:'students', name:'Students', permissions:['1','2','4','5']},
		]},
		{screen:'scheduling_data', name:'Scheduling Data', permissions:['1','2','3','4','5'], subscreens: [ 
			{subscreen:'requests', name:'Requests', permissions:['1','2','3','4','5']}, 
			{subscreen:'sections', name:'Sections', permissions:['1','2','3','4','5']}, 
		]},
		{screen:'magnetboard', name:'Magnetboard', permissions:['1','2','3','4','5'], subscreens: []},
		{screen:'report_a_bug', name:'Report a Bug', permissions:['1','2','3','4','5'], subscreens:[]},
	];

	const changeSubscreen = (new_screen, new_subscreen) => {
		setTopic(null);
		setSis(null);
		setScreen(new_screen);
		setOpenScreenTab(new_screen);
		setSubscreen(new_subscreen);
	}
	
	useEffect(() => {
		// Check if user is logged in
		// If not, send them to log in screen with a redirect to this screen
		if(authComplete && !isAuth)
		{
			history.push({
				pathname: '/app/login',
				state: {redirect: `/app/${schoolType}/documentation/account`}
			});
		}
		else if(authComplete && !schoolInfoLoading)
		{
			setLoadScreen('documentation');
			setIsLoading(false);
		}
	}, [isAuth, authComplete, schoolInfoLoading, history, schoolType, setLoadScreen]);

	//console.log({screen, subscreen, openScreenTab});
	
	return (
		<div id='documentation-main-container'>
			<div id='documentation-sidebar'>
				<div className='secondary-sidebar-header'><FontAwesomeIcon icon={faBook} style={{marginRight:'12px'}}></FontAwesomeIcon>Docs</div>
				{app_screens.map((screen_option, i) => {
					const has_permission = (userInfo) ? screen_option.permissions.includes(userInfo.role_id) : false;
					const has_subscreens = screen_option.subscreens.length > 0;
					const is_screen_selected = screen_option.screen === openScreenTab;
					
					return (
						<div key={i}>
						{has_permission && 
							<Link className={`documentation-sidebar-btn ${has_subscreens ? 'documentation-sidebar-btn-with-icon' : ''} ${(is_screen_selected) ? 'documentation-sidebar-btn-selected' : ''}`} to={`/app/${schoolType}/documentation/${screen_option.screen}`} onClick={has_subscreens ? ()=>setOpenScreenTab(screen_option.screen) : ()=>changeSubscreen(screen_option.screen, screen_option.screen)}>
								<div>{screen_option.name}</div>
								{has_subscreens &&
									<FontAwesomeIcon icon={is_screen_selected ? faChevronUp : faChevronDown}></FontAwesomeIcon>
								}
							</Link>
						}
						{(has_permission && has_subscreens && is_screen_selected) &&
							<div className='documentation-sidebar-subscreen-container'>
							{screen_option.subscreens.map(subscreen_option => {
								const is_subscreen_selected = subscreen_option.subscreen === subscreen;

								const has_subscreen_permission = userInfo ? subscreen_option.permissions.includes(userInfo.role_id) : false;
								if(!has_subscreen_permission) return null;

								return (
									<Link className={`documentation-sidebar-btn ${is_subscreen_selected ? 'documentation-sidebar-btn-selected' : ''}`} to={`/app/${schoolType}/documentation/${screen_option.screen}/${subscreen_option.subscreen}`} key={`subscreen-tab-btn-${subscreen_option.name}`} onClick={() => changeSubscreen(screen_option.screen, subscreen_option.subscreen)}>
										<div>{subscreen_option.name}</div>
									</Link>
								)
							})}
							</div>
						}

						</div>
					)
				})}
			</div>
			<div id='documentation-main-content'>
					<div id='documentation-main-content-inner'>
					{isLoading ?
						(
							<div className='center-text'>
								<img src={require('../../images/balls.gif')} alt='Loading...' style={{height:'40px'}}/>
							</div>
						):
						(
							<>
							{(() => {
								switch(subscreen) {
									case 'getting_started':
										return <GetStartedDocs/>;
									case 'schedule_structure':
										if(isSupervisor) return <GetStartedDocs/>;
										return <ScheduleStructureDocs topic={topic} setTopic={setTopic} schoolType={schoolType} scrollToInstructions={scrollToInstructions}/>;
									case 'upload_data':
										if(isSupervisor) return <GetStartedDocs/>;
										return <UploadDataDocs topic={topic} setTopic={setTopic} sis={sis} setSis={setSis} schoolType={schoolType}/>;
									case 'manage_departments':
										if(isSupervisor) return <GetStartedDocs/>;
										return <ManageDepartmentsDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType} scrollToInstructions={scrollToInstructions}/>;
									case 'edit_constraints':
										return <EditConstraintsDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'create_sections':
										return <CreateSectionsDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'place_sections':
										return <PlaceSectionsDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'place_students':
										if(isSupervisor) return <GetStartedDocs/>;
										return <PlaceStudentsDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'courses':
										return <CoursesDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'teachers':
										return <TeachersDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'classrooms':
										return <ClassroomsDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'students':
										if(isSupervisor) return <GetStartedDocs/>;
										return <StudentsDocs topic={topic} setTopic={setTopic} schoolType={schoolType}/>;
									case 'requests':
										return <RequestsDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'sections':
										return <SectionsDocs topic={topic} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									case 'magnetboard':
										return <MagnetboardDocs topic={magnetboardTopicFromUrl} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>;
									default:
										return (screen === 'magnetboard') ? <MagnetboardDocs topic={magnetboardTopicFromUrl} setTopic={setTopic} userInfo={userInfo} schoolType={schoolType}/>
											: (screen === 'report_a_bug') ? <ReportABugDocs/>
											: <GetStartedDocs/>;
								}
							})()}
							</>
						)
					}
				</div>
			</div>
		</div>
	);
}
